import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import CourseCard from '../../components/CourseCard/CourseCard';
import * as firebaseCourses from '../../services/firebaseCourses';
import { testimonials } from '../../utils/homeData';
import './Home.css';

const INFOBLOOM_WHATSAPP_URL = 'https://wa.me/2349014088590?text=Hi%2C%20I%27m%20interested%20in%20becoming%20a%20course%20creator%20on%20Infobloom.';

export default function Home() {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const popularCoursesRef = useRef(null);
  const testimonialsRef = useRef(null);
  const certRef = useRef(null);
  const trendingRef = useRef(null);
  const trustedRef = useRef(null);
  const [inView, setInView] = useState({});
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [coursesError, setCoursesError] = useState('');
  const creatorRef = useRef(null);

  useEffect(() => {
    if (location.hash === '#popular-courses' && popularCoursesRef.current) {
      popularCoursesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  useEffect(() => {
    const sections = [
      { ref: popularCoursesRef, key: 'popular' },
      { ref: testimonialsRef, key: 'testimonials' },
      { ref: certRef, key: 'cert' },
      { ref: creatorRef, key: 'creator' },
      { ref: trendingRef, key: 'trending' },
      { ref: trustedRef, key: 'trusted' },
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const key = entry.target.dataset.animateSection;
            if (key) setInView((prev) => ({ ...prev, [key]: true }));
          }
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.1 }
    );
    sections.forEach(({ ref, key }) => {
      if (ref.current) {
        ref.current.dataset.animateSection = key;
        observer.observe(ref.current);
      }
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingCourses(true);
    setCoursesError('');
    firebaseCourses.listCoursesForHome()
      .then((list) => {
        if (!isMounted) return;
        setCourses(list || []);
      })
      .catch(() => {
        if (!isMounted) return;
        setCoursesError('Could not load courses. Please refresh the page.');
      })
      .finally(() => {
        if (isMounted) setLoadingCourses(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="udemy-home">
      {/* Hero - white card left, figure + floating icons right on blue */}
      <section className="udemy-hero">
        <div className="udemy-hero-inner">
          <div className="udemy-hero-card udemy-hero-card-animate">
            <h1 className="udemy-hero-title">Learn with Infobloom</h1>
            <p className="udemy-hero-desc">Expert-led courses and certifications built for real-world impact. One place to grow your career, at your pace.</p>
            <div className="udemy-hero-btns">
              <Link to={isAuthenticated ? '/my-learning' : '/login'} className="udemy-hero-btn udemy-hero-btn-primary">
                Start learning
              </Link>
              <Link to="/#popular-courses" className="udemy-hero-btn udemy-hero-btn-outline">Explore courses</Link>
            </div>
          </div>
          <div className="udemy-hero-right udemy-hero-right-animate">
            <div className="udemy-hero-pattern" aria-hidden="true" />
            <img
              src={`${process.env.PUBLIC_URL || ''}/image1.jpg`}
              alt=""
              className="udemy-hero-figure"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.add('udemy-hero-fallback-visible'); }}
            />
            <div className="udemy-hero-fallback">
              <img src={`${process.env.PUBLIC_URL || ''}/classroom.png`} alt="" />
            </div>
            <div className="udemy-hero-icon udemy-hero-icon-top udemy-hero-icon-float">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M3 18v-6h4l2-4 2 8 4-6h4v8" /></svg>
            </div>
            <div className="udemy-hero-icon udemy-hero-icon-left udemy-hero-icon-float">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </div>
            <div className="udemy-hero-icon udemy-hero-icon-bottom udemy-hero-icon-float">
              <svg viewBox="0 0 24 24" fill="#14b8a6" stroke="#14b8a6" strokeWidth="1"><path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-4.5 3 2-7L2 9h7l3-7z" /></svg>
            </div>
          </div>
        </div>
      </section>

      {/* Course cards - first row */}
      <section id="popular-courses" className={`udemy-section udemy-courses ${inView.popular ? 'udemy-inview' : ''}`} ref={popularCoursesRef}>
        <h2 className="udemy-section-title">Popular courses</h2>
        {coursesError && <p className="udemy-courses-error" role="alert">{coursesError}</p>}
        <div className="udemy-courses-grid">
          {loadingCourses && courses.length === 0 ? (
            <div className="udemy-courses-loading" aria-label="Loading courses" />
          ) : (
            (() => {
              const popular = courses.filter((c) => c.isPopular || c.is_popular);
              const list = popular.length > 0 ? popular : courses;
              if (!list.length) {
                return <p className="udemy-courses-empty">No courses available yet.</p>;
              }
              return list.map((course, idx) => (
                <div key={course.id} className="udemy-course-card-wrap" style={{ animationDelay: `${idx * 0.07}s` }}>
                  <CourseCard course={course} />
                </div>
              ));
            })()
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className={`udemy-section udemy-testimonials ${inView.testimonials ? 'udemy-inview' : ''}`} ref={testimonialsRef}>
        <h2 className="udemy-section-title">Join others transforming their lives through learning.</h2>
        <div className="udemy-testimonials-grid">
          {testimonials.map((t, i) => (
            <div key={i} className="udemy-testimonial-card" style={{ animationDelay: `${i * 0.08}s` }}>
              <p className="udemy-testimonial-quote">"{t.quote}"</p>
              <div className="udemy-testimonial-author">
                <span className="udemy-testimonial-avatar">{t.name.charAt(0)}</span>
                <div>
                  <p className="udemy-testimonial-name">{t.name}</p>
                  <p className="udemy-testimonial-course">{t.course}</p>
                </div>
              </div>
              <Link to="/" className="udemy-testimonial-view">View course →</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Get certified by Infobloom */}
      <section className={`udemy-section udemy-cert-dark ${inView.cert ? 'udemy-inview' : ''}`} ref={certRef}>
        <div className="udemy-cert-dark-inner">
          <div className="udemy-cert-dark-content">
            <h2 className="udemy-cert-dark-title">Get certified by <span className="infobloom-brand">Infobloom</span></h2>
            <p className="udemy-cert-dark-subtitle">Get certified and get ahead in your career</p>
          </div>
        </div>
      </section>

      {/* Become a course creator - WhatsApp CTA */}
      <section className={`udemy-section udemy-creator ${inView.creator ? 'udemy-inview' : ''}`} ref={creatorRef}>
        <div className="udemy-creator-inner">
          <h2 className="udemy-creator-title">{t('home.creator.title')}</h2>
          <p className="udemy-creator-desc">{t('home.creator.desc')}</p>
          <p className="udemy-creator-cta">{t('home.creator.cta')}</p>
          <a href={INFOBLOOM_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="udemy-creator-btn">
            <svg className="udemy-creator-btn-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('home.creator.button')}
          </a>
        </div>
      </section>

      {/* Trending courses */}
      <section className={`udemy-section udemy-trending-courses ${inView.trending ? 'udemy-inview' : ''}`} ref={trendingRef}>
        <h2 className="udemy-section-title">Trending courses</h2>
        <div className="udemy-courses-grid">
          {loadingCourses && courses.length === 0 ? (
            <div className="udemy-courses-loading" aria-label="Loading courses" />
          ) : (
            (() => {
              const trending = courses.filter((c) => c.isTrending || c.is_trending);
              const list = trending.length > 0 ? trending : courses;
              if (!list.length) {
                return <p className="udemy-courses-empty">No courses available yet.</p>;
              }
              return list.map((course, idx) => (
                <div key={course.id} className="udemy-course-card-wrap" style={{ animationDelay: `${idx * 0.06}s` }}>
                  <CourseCard course={course} />
                </div>
              ));
            })()
          )}
        </div>
      </section>

      {/* Trusted by - above footer is in Footer */}
      <section className={`udemy-trusted ${inView.trusted ? 'udemy-inview' : ''}`} ref={trustedRef}>
        <div className="udemy-trusted-inner">
          <p className="udemy-trusted-text">Trusted by over 15,000 companies and millions of learners around the world.</p>
        </div>
      </section>
    </div>
  );
}
